import { useEffect, useState } from "react";
import {
  generateOutputs,
  generateReportVariant,
  generateCustomReport,
  getArtifacts,
  getCanvas,
  runConsistency,
  type OutputsResponse,
} from "./api";
import type {
  ArtifactBundle,
  CanvasSession,
  ConsistencyResult,
  VariantKind,
} from "./schema";
import { PhaseTracker } from "./PhaseTracker";
import { ReminderPicker } from "./scheduler/ReminderPicker";
import { useIsFreeTier } from "../users/useMe";
import { PaidFeatureBadge } from "../users/PaidFeatureBadge";
import { UpgradeModal } from "../users/UpgradeModal";

interface Props {
  sessionId: string;
  onBack?: () => void;
  onHome?: () => void;
  onOpenCoach?: () => void;
}

const VARIANT_LABELS: Record<VariantKind, string> = {
  "gap-summary": "Gap summary",
  scorecard: "Deployment scorecard",
  consistency: "Consistency report",
  "one-pager": "Executive one-pager",
};
const VARIANT_KINDS: VariantKind[] = [
  "gap-summary",
  "scorecard",
  "consistency",
  "one-pager",
];

export function OutputsScreen({ sessionId, onBack, onHome, onOpenCoach }: Props) {
  const isFree = useIsFreeTier();
  const [upgradePrompt, setUpgradePrompt] = useState<string | null>(null);
  const [session, setSession] = useState<CanvasSession | null>(null);
  const [checks, setChecks] = useState<ConsistencyResult[] | null>(null);
  const [outputs, setOutputs] = useState<OutputsResponse | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactBundle | null>(null);
  const [busy, setBusy] = useState<"checks" | "outputs" | null>(null);
  const [variantBusy, setVariantBusy] = useState<VariantKind | null>(null);
  const [bespokePrompt, setBespokePrompt] = useState("");
  const [bespokeBusy, setBespokeBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCanvas(sessionId).then(setSession).catch((e) => setError((e as Error).message));
  }, [sessionId]);

  useEffect(() => {
    // Returning-user case: load persisted artifacts (signed URLs) if they exist.
    // 404 (no artifacts yet) is expected and silent.
    getArtifacts(sessionId)
      .then(setArtifacts)
      .catch(() => setArtifacts(null));
  }, [sessionId]);

  const handleRunChecks = async () => {
    setBusy("checks");
    setError(null);
    try {
      const r = await runConsistency(sessionId);
      setChecks(r);
      setSession(await getCanvas(sessionId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleGenerate = async () => {
    setBusy("outputs");
    setError(null);
    try {
      const o = await generateOutputs(sessionId);
      setOutputs(o);
      setArtifacts(o.artifacts);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleVariant = async (v: VariantKind) => {
    setVariantBusy(v);
    setError(null);
    try {
      const r = await generateReportVariant(sessionId, v);
      triggerDownload(r.url, r.filename);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setVariantBusy(null);
    }
  };

  const handleBespoke = async () => {
    const prompt = bespokePrompt.trim();
    if (!prompt) return;
    setBespokeBusy(true);
    setError(null);
    try {
      const r = await generateCustomReport(sessionId, prompt);
      triggerDownload(r.url, r.filename);
      setBespokePrompt("");
      // Refresh so the new report shows in the list (works whether or not
      // outputs was generated this session, since effectiveArtifacts prefers it).
      const fresh = await getArtifacts(sessionId).catch(() => null);
      if (fresh) {
        setArtifacts(fresh);
        setOutputs((prev) => (prev ? { ...prev, artifacts: fresh } : prev));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBespokeBusy(false);
    }
  };

  if (!session) return <div className="canvas canvas--loading">Loading…</div>;

  const activeChecks = checks ?? session.consistencyChecks.results;
  // Prefer the bundle returned from a just-completed Generate; fall back to
  // the persisted bundle fetched on mount.
  const effectiveArtifacts: ArtifactBundle | null = outputs?.artifacts ?? artifacts;

  return (
    <div className="canvas">
      <PhaseTracker session={session} />

      <div className="canvas__panel">
        <p className="canvas__eyebrow">Step 08 — Output</p>
        <h1 className="canvas__display">Your review is ready.</h1>
        <p className="canvas__lede">
          Run the cross-phase consistency checks, then generate the JSON state and Word
          governance report.
        </p>

        {error && <div className="banner banner--error">{error}</div>}

        <section className="outputs">
          <header className="outputs__header">
            <div>
              <div className="outputs__title">01 — Consistency checks</div>
              <div className="outputs__sub">
                Rule-based cross-phase validation. No LLM call.
              </div>
            </div>
            <button
              className="btn btn--ghost"
              disabled={busy !== null}
              onClick={() => void handleRunChecks()}
            >
              {busy === "checks" ? "Running…" : activeChecks.length > 0 ? "Re-run" : "Run checks"}
            </button>
          </header>
          {activeChecks.length > 0 && (
            <ul className="checks">
              {activeChecks.map((c) => (
                <li key={c.code} className={`check check--${c.verdict}`}>
                  <span className="check__code mono">{c.code}</span>
                  <div className="check__body">
                    <div className="check__label">{c.label}</div>
                    <div className="check__detail">{c.detail}</div>
                  </div>
                  <span className={`check__verdict check__verdict--${c.verdict}`}>
                    {c.verdict.toUpperCase()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="outputs">
          <header className="outputs__header">
            <div>
              <div className="outputs__title">02 — Generate three artifacts</div>
              <div className="outputs__sub">
                Baseline Record (verbatim transcript) · canvas.json (machine-readable
                state) · Governance Report (narrative review with grade).
              </div>
            </div>
            <button
              className="btn btn--primary"
              disabled={busy !== null}
              onClick={() => void handleGenerate()}
            >
              {busy === "outputs" ? "Generating…" : effectiveArtifacts ? "Regenerate" : "Generate"}
            </button>
          </header>

          {outputs && effectiveArtifacts && (
            <>
              <div className="grade">
                <div className="grade__panel">
                  <div className="grade__eyebrow">Overall grade</div>
                  <div className={`grade__letter grade__letter--${gradeColor(outputs.grade)}`}>
                    {outputs.grade}
                  </div>
                  <div className="grade__composite mono">{outputs.composite}/5.00</div>
                </div>
                <div className="grade__downloads">
                  <a
                    className="btn btn--primary"
                    href={effectiveArtifacts.baselineRecord.url}
                    download={effectiveArtifacts.baselineRecord.filename}
                  >
                    ↓ baseline-record.docx
                  </a>
                  <a
                    className="btn btn--ghost"
                    href={effectiveArtifacts.canvasJson.url}
                    download={effectiveArtifacts.canvasJson.filename}
                  >
                    ↓ canvas.json
                  </a>
                  <a
                    className="btn btn--ghost"
                    href={effectiveArtifacts.governanceReport.url}
                    download={effectiveArtifacts.governanceReport.filename}
                  >
                    ↓ governance-report.docx
                  </a>
                </div>
              </div>
              <p className="outputs__reassurance">
                Your complete answers are now captured in{" "}
                <strong>{effectiveArtifacts.baselineRecord.filename}</strong>.
                Nothing you entered has been lost.
              </p>
            </>
          )}

          {!outputs && effectiveArtifacts && (
            <div className="grade">
              <div className="grade__panel grade__panel--returning">
                <div className="grade__eyebrow">Already generated</div>
                <p className="grade__returning-note">
                  Downloads below are signed for 7 days.
                </p>
              </div>
              <div className="grade__downloads">
                <a
                  className="btn btn--primary"
                  href={effectiveArtifacts.baselineRecord.url}
                  download={effectiveArtifacts.baselineRecord.filename}
                >
                  ↓ baseline-record.docx
                </a>
                <a
                  className="btn btn--ghost"
                  href={effectiveArtifacts.canvasJson.url}
                  download={effectiveArtifacts.canvasJson.filename}
                >
                  ↓ canvas.json
                </a>
                <a
                  className="btn btn--ghost"
                  href={effectiveArtifacts.governanceReport.url}
                  download={effectiveArtifacts.governanceReport.filename}
                >
                  ↓ governance-report.docx
                </a>
              </div>
            </div>
          )}
        </section>

        {effectiveArtifacts && (
          <section className="outputs">
            <header className="outputs__header">
              <div>
                <div className="outputs__title">03 — On-demand reports {isFree && <PaidFeatureBadge />}</div>
                <div className="outputs__sub">
                  Focused variants generated from the same canvas data.
                </div>
              </div>
            </header>
            {isFree ? (
              <div className="outputs__variants outputs__variants--locked" onClick={() => setUpgradePrompt("report-variant")}>
                <p>Generate gap summary, scorecard, consistency report, and one-pager — <PaidFeatureBadge /></p>
              </div>
            ) : (
              <>
                <div className="outputs__variant-row">
                  {VARIANT_KINDS.map((v) => (
                    <button
                      key={v}
                      className="btn btn--ghost"
                      disabled={variantBusy !== null}
                      onClick={() => void handleVariant(v)}
                    >
                      {variantBusy === v ? "Generating…" : VARIANT_LABELS[v]}
                    </button>
                  ))}
                </div>

                <div className="outputs__bespoke">
                  <label className="outputs__bespoke-label" htmlFor="bespoke">
                    Or request a custom report
                  </label>
                  <p className="outputs__bespoke-hint">
                    Describe any report you need — e.g. "a risk register for the board" or
                    "an auditor-facing summary of the Safety layer." Generated from your canvas.
                  </p>
                  <div className="outputs__bespoke-row">
                    <textarea
                      id="bespoke"
                      className="outputs__bespoke-input"
                      rows={2}
                      maxLength={600}
                      placeholder="What report would you like?"
                      value={bespokePrompt}
                      onChange={(e) => setBespokePrompt(e.target.value)}
                      disabled={bespokeBusy}
                    />
                    <button
                      className="btn btn--primary"
                      disabled={bespokeBusy || !bespokePrompt.trim()}
                      onClick={() => void handleBespoke()}
                    >
                      {bespokeBusy ? "Generating…" : "Generate"}
                    </button>
                  </div>
                  {effectiveArtifacts?.bespoke && effectiveArtifacts.bespoke.length > 0 && (
                    <ul className="outputs__bespoke-list">
                      {effectiveArtifacts.bespoke.map((b) => (
                        <li key={b.id} className="outputs__bespoke-item">
                          <span className="outputs__bespoke-prompt">{b.prompt}</span>
                          <a href={b.url} download={b.filename}>↓ download</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {effectiveArtifacts && session && (
          <section className="outputs">
            <header className="outputs__header">
              <div>
                <div className="outputs__title">04 — Schedule a revisit {isFree && <PaidFeatureBadge />}</div>
                <div className="outputs__sub">
                  We'll email you on the date you pick — once, with an ICS file you can drop into your calendar.
                </div>
              </div>
            </header>
            {isFree ? (
              <div className="reminder-picker reminder-picker--disabled" onClick={() => setUpgradePrompt("reminder")}>
                <div className="reminder-picker__stamp">Paid feature</div>
                <p className="reminder-picker__note">
                  Schedule a revisit email + ICS to keep your canvas honest. <PaidFeatureBadge />
                </p>
              </div>
            ) : (
              <ReminderPicker
                sessionId={sessionId}
                currentReminderDateMs={session.nextReviewDate ?? null}
                onSaved={async () => {
                  const fresh = await getCanvas(sessionId);
                  setSession(fresh);
                }}
              />
            )}
          </section>
        )}

        {onOpenCoach && effectiveArtifacts && (
          <section className="outputs">
            <header className="outputs__header">
              <div>
                <div className="outputs__title">05 — Ask the coach {isFree && <PaidFeatureBadge />}</div>
                <div className="outputs__sub">
                  Open a chat about your gaps, a layer you want to strengthen, or a recent incident.
                </div>
              </div>
              <button
                className="btn btn--primary"
                onClick={isFree ? () => setUpgradePrompt("coach") : onOpenCoach}
              >
                Talk to the coach →
              </button>
            </header>
          </section>
        )}

        <UpgradeModal
          open={upgradePrompt !== null}
          feature={upgradePrompt ?? ""}
          onClose={() => setUpgradePrompt(null)}
        />

        {(onBack || onHome) && (
          <footer className="canvas__actions">
            {onHome && (
              <button className="btn btn--ghost" onClick={onHome}>← Home</button>
            )}
            {onBack && (
              <button className="btn btn--ghost" onClick={onBack}>Review answers →</button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function gradeColor(g: string): "high" | "mid" | "low" {
  if (g === "A" || g === "B") return "high";
  if (g === "C") return "mid";
  return "low";
}

// Triggers a file download from a cross-origin URL without opening a tab.
// The signed URL carries Content-Disposition: attachment, so a programmatic
// anchor click hands the response to the browser's download chrome.
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
